import { useCallback } from 'react';

import { PreviewSnippet, Skeleton, SxProps, notify } from '@cast/design-system';

import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { applyYamlPreview } from '@cast/utils';

import { InstallComponentFallback } from '../InstallComponentFallback';

type Props = {
  code?: string;
  sx?: SxProps;
  isLoading?: boolean;
  skeletonHeight?: number;
  errorMessage?: string;
  error?: unknown;
  refetch?: () => void;
  testId?: string;
  onCopied?: () => void;
};

export const AgentCodeSnippet = ({
  code,
  sx,
  isLoading,
  skeletonHeight = 78,
  error,
  refetch,
  errorMessage = 'Failed to load agent script',
  testId = 'agent-code-snippet',
  onCopied,
}: Props) => {
  const { currentOrganization } = useOrganizations();

  const handleRenderPreview = useCallback(
    (_code: string) => {
      return applyYamlPreview(_code, currentOrganization?.id);
    },
    [currentOrganization?.id]
  );

  const handleCopy = useCallback(
    (_code: string) => {
      notify.success('Copied to clipboard');
      onCopied?.();
    },
    [onCopied]
  );

  if (error) {
    return (
      <InstallComponentFallback
        message={errorMessage}
        refetch={refetch}
        testId={testId}
      />
    );
  }

  if (isLoading) {
    return (
      <Skeleton width="100%" height={skeletonHeight} data-testid={testId} />
    );
  }

  return (
    <PreviewSnippet
      code={code || ''}
      renderPreview={handleRenderPreview}
      onCopy={handleCopy}
      sx={sx}
      testId={testId}
    />
  );
};
